import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, computed, inject, input, model, OnInit, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import {
  NestedList,
  NestedListAction,
  NestedListDropOtherListEvent,
  NestedListOptions,
} from '@shared/components/nested-list/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { UtilsService } from '@shared/services';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'cipo-nested-list',
  imports: [CdkDropList, CdkDrag, CipoFormModule, MatMenuModule],
  templateUrl: './nested-list.component.html',
  styleUrls: ['./nested-list.component.scss'],
})
export class NestedListComponent implements OnInit {
  utilsService = inject(UtilsService);

  // List of items to display
  list = model<NestedList[]>([]);
  // Row actions to display on each item, left and right actions combined
  rowActions = input<NestedListAction[]>([]);
  // Top actions to display in the right of Add New button
  topActions = input<NestedListAction[]>([]);
  // Options to configure the nested list component
  options = input<NestedListOptions>(null);
  // If connectedListTo is set, this list can accept items dropped from another list with the same connectedListTo value
  connectedListTo = input<string>();
  // Event emitted when the "Add New" button is clicked
  addNewAction = output();
  // Event emitted when an item is dropped from another list
  dropOtherList = output<NestedListDropOtherListEvent>();
  // Signal to indicate if anything has changed in the list
  anythingChanged = model<boolean>(false);

  // Computed properties for left row actions
  leftRowActions = computed(() => this.rowActions().filter(action => action.location === 'start'));

  // Computed properties for right row actions
  rightRowActions = computed(() => this.rowActions().filter(action => action.location === 'end'));

  searchControl = new FormControl('');
  searchValue = signal('');
  filteredList = computed(() => {
    const searchValue = this.searchValue();
    return this.list()?.filter(o => o.visible && o.title?.toLowerCase()?.includes(searchValue?.toLowerCase()));
  });

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.default))
      .subscribe(value => this.searchValue.set(value));
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      this.utilsService.moveItemsInSignalArray(this.list, event.previousIndex, event.currentIndex);
      this.list.update(list => list.map((item, index) => ({ ...item, position: index })));
    } else {
      this.dropOtherList.emit({
        previousIndex: event.previousIndex,
        currentIndex: event.currentIndex,
        currentList: this.list(),
      });
    }
    this.anythingChanged.set(true);
  }

  clearInput() {
    this.searchControl.setValue('');
  }
}
